// The CONNECTION DIAGRAM on the Memory rail (Signal re-layout).
//
// WHAT IS REAL: the focused Node's own edges — its parsed `[[wikilink]]` targets (outbound) and its
// backlinks (inbound, carrying the source Node's human title, G-197). That is a ONE-HOP neighbourhood, not a
// global graph, and the projection carries NO coordinates. So the layout is COMPUTED from the real edges:
// outbound neighbours fan to the right of the focused Node, inbound to the left, at evenly-spaced angles
// derived from each neighbour's index. It is a pure function of the edge list — deterministic, no randomness,
// so the same Node always draws the same picture (a jittering map is a bug, not a flourish).
//
// The diagram is PRESENTATIONAL: navigation lives in the article's "Connected memories" list, which is a real
// list of real buttons. Duplicating those clicks onto SVG circles would add an inaccessible second control
// set for no new information. When a Node has no edges at all the whole region is OMITTED by the rail —
// an empty circle claiming to be a map would say something untrue.

import type { BacklinkView, MemoryNodeView } from '../know-projections.ts';

/** One real edge of the focused Node — an outbound `[[wikilink]]` or an inbound backlink. */
export interface MemoryEdge {
  /** Stable list key, and the edge's behavioural marker (`link-…` / `backlink-…`). */
  readonly key: string;
  /** What focusing this edge re-reads — the `[[wikilink]]` target token, or the linking Node's id. */
  readonly nodeId: string;
  /** The VISIBLE label: a backlink shows its source Node's human title, never the raw id (G-197). */
  readonly label: string;
  readonly relation: string;
  readonly direction: 'out' | 'in';
}

/** The focused Node's real edges: its parsed `[[wikilinks]]` out, then the Nodes that link in. */
export function memoryEdges(
  node: MemoryNodeView,
  backlinks: readonly BacklinkView[],
): readonly MemoryEdge[] {
  return [
    ...node.links.map((link) => ({
      key: `link-${link.target}`,
      nodeId: link.target,
      label: link.target,
      relation: link.relation,
      direction: 'out' as const,
    })),
    ...backlinks.map((backlink) => ({
      key: `backlink-${backlink.fromNodeId}`,
      nodeId: backlink.fromNodeId,
      label: backlink.title,
      relation: backlink.relation,
      direction: 'in' as const,
    })),
  ];
}

/** One plotted neighbour — a computed position, never a stored one. */
export interface MemoryPlotPoint {
  readonly key: string;
  readonly label: string;
  readonly relation: string;
  readonly direction: 'out' | 'in';
  readonly x: number;
  readonly y: number;
}

/** The diagram's geometry, in the SVG's own view units. */
const VIEW_WIDTH = 320;
const VIEW_HEIGHT = 150;
const CENTRE_X = VIEW_WIDTH / 2;
const CENTRE_Y = VIEW_HEIGHT / 2;
const RADIUS = 58;
/** Half the angular fan each side gets, in radians (≈62°) — wide enough to separate, narrow enough to read. */
const FAN = 1.08;

/** Round to two places so the rendered geometry is byte-stable across runs. */
function fixed(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Place one side's neighbours on an arc. A single neighbour sits on the axis; more than one fan evenly
 * between `-FAN` and `+FAN` around it. Index-derived — the same edges always produce the same points.
 */
function placeSide(edges: readonly MemoryEdge[], baseAngle: number): MemoryPlotPoint[] {
  return edges.map((edge, index) => {
    const spread = edges.length === 1 ? 0 : (index / (edges.length - 1)) * 2 - 1;
    const angle = baseAngle + spread * FAN;
    return {
      key: edge.key,
      label: edge.label,
      relation: edge.relation,
      direction: edge.direction,
      x: fixed(CENTRE_X + RADIUS * Math.cos(angle)),
      y: fixed(CENTRE_Y + RADIUS * Math.sin(angle)),
    };
  });
}

/** Plot the focused Node's real edges. Outbound fans right (angle 0), inbound left (angle π). */
export function plotConnections(edges: readonly MemoryEdge[]): readonly MemoryPlotPoint[] {
  return [
    ...placeSide(
      edges.filter((edge) => edge.direction === 'out'),
      0,
    ),
    ...placeSide(
      edges.filter((edge) => edge.direction === 'in'),
      Math.PI,
    ),
  ];
}

/** The diagram — the focused Node at the centre with one spoke per real edge. */
export function MemoryConnectionMap({
  title,
  edges,
}: {
  readonly title: string;
  readonly edges: readonly MemoryEdge[];
}): React.JSX.Element {
  const points = plotConnections(edges);
  return (
    <svg
      viewBox={`0 0 ${String(VIEW_WIDTH)} ${String(VIEW_HEIGHT)}`}
      role="img"
      aria-label={`${title} and its ${String(edges.length)} connected ${edges.length === 1 ? 'memory' : 'memories'}`}
      data-memory-map
    >
      {/* Index-suffixed keys: `point.key` is the target marker, and a Node may link to the same target
          under two relations (memory de-duplicates by relation+target), so the marker alone can repeat. */}
      {points.map((point, index) => (
        <line
          key={`e-${point.key}-${String(index)}`}
          x1={CENTRE_X}
          y1={CENTRE_Y}
          x2={point.x}
          y2={point.y}
          stroke="var(--border-strong)"
          strokeWidth={1}
        />
      ))}
      {points.map((point, index) => (
        <circle
          key={`p-${point.key}-${String(index)}`}
          cx={point.x}
          cy={point.y}
          r={4.5}
          fill={point.direction === 'out' ? 'var(--accent)' : 'var(--border-strong)'}
        >
          <title>{`${point.direction === 'out' ? 'links to' : 'linked from'} ${point.label} · ${point.relation}`}</title>
        </circle>
      ))}
      <FocusMarker title={title} />
    </svg>
  );
}

/** The focused Node itself — a ringed dot at the centre of its own neighbourhood. */
function FocusMarker({ title }: { readonly title: string }): React.JSX.Element {
  return (
    <>
      <circle
        cx={CENTRE_X}
        cy={CENTRE_Y}
        r={9}
        fill="none"
        stroke="var(--forest)"
        strokeWidth={1}
      />
      <circle cx={CENTRE_X} cy={CENTRE_Y} r={5.5} fill="var(--forest)">
        <title>{title}</title>
      </circle>
    </>
  );
}
